import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _get from 'lodash/get';

import { AnimateFrom } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.types.ts';
import { ContentWrapper } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.style.tsx';
import { Wrapper as HeaderWrapper } from '@mlb/react-patterns/components/AnimatedModal/Header/header.style.tsx';
import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import FONTS from '@mlb/react-patterns/style/fonts.ts';

import { Tracking } from 'bdvp-tracking';
import { useKeyPress, useMedia } from 'bdvp-common/src/hooks';
import { keyConstants } from 'bdvp-common/src/utils';
import ModalContainer from 'bdvp-common/src/containers/Modal';

import FormController from './FormController';

const { TAB } = keyConstants;
const keysToTrack = [TAB];

export const ModalOverride = styled(ModalContainer)`
    font-family: ${FONTS.primaryFont};

    @supports (padding-top: constant(safe-area-inset-top)) {
        --safe-area-inset-top: constant(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: constant(safe-area-inset-bottom, 0);
    }

    @supports (padding-top: env(safe-area-inset-top)) {
        --safe-area-inset-top: env(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: env(safe-area-inset-bottom, 0);
    }

    ${media.mdmin`
        height: 624px;
        max-height: 95vh;
        max-width: 96vw;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        padding-bottom: 0;

        ${HeaderWrapper} {
            padding: 20px 20px 10px 20px;
        }
    `}

    ${ContentWrapper} {
        height: 100%;
    }
`;

const CreationModal = ({
    dictionary,
    name,
    reel,
    validateReel,
    createReel,
    createReelLoading,
    createReelData,
    createReelError,
    config,
    userId,
    history,
    closeReelCreationModal,
}) => {

    const firstModalNode = React.useRef(null);
    const lastModalNode = React.useRef(null);
    const [
        title,
        setTitle,
    ] = useState(_get(dictionary, 'create_your_reel', 'Create Your Reel'));

    const keyPressCallbackProps = {
        cb: (event) => {

            if (event.target === lastModalNode.current) {

                event.preventDefault();
                return firstModalNode.current.focus();

            }

        },
        shiftCB: (event) => {

            if (event.target === firstModalNode.current) {

                event.preventDefault();
                return lastModalNode.current.focus();

            }

        },
    };

    useKeyPress(keysToTrack, keyPressCallbackProps);

    const {
        animated,
        width,
        height,
    } = useMedia(
        [`(min-width: ${BREAKPOINTS.md})`],
        [
            {
                animated: false,
                width: '721px',
                height: 'auto',
            },
        ],
        {
            animated: true,
            width: '100%',
            height: '99%',
        }
    );

    return (
        <ModalOverride
            name={name}
            animate={animated}
            animateFrom={animated ? AnimateFrom.BOTTOM : undefined}
            closeButtonRef={firstModalNode}
            headerTitle={title}
            width={width}
            height={height}
            overlay
            position={{
                bottom: 0,
                left: 0,
            }}
            handlers={{
                onOpened: () => {

                    firstModalNode.current.focus();

                },
                onClosed: () => Tracking.trackReelCreationModalClose(),
            }}
        >
            <FormController
                dictionary={dictionary}
                reel={reel}
                validateReel={validateReel}
                createReel={createReel}
                createReelLoading={createReelLoading}
                createReelData={createReelData}
                createReelError={createReelError}
                lastModalNode={lastModalNode}
                config={config}
                setModalTitle={setTitle}
                userId={userId}
                history={history}
                closeReelCreationModal={closeReelCreationModal}
            />
        </ModalOverride>
    );

};

CreationModal.propTypes = {
    dictionary: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    reel: PropTypes.object.isRequired,
    validateReel: PropTypes.func.isRequired,
    createReel: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    createReelLoading: PropTypes.bool,
    createReelData: PropTypes.object,
    createReelError: PropTypes.object,
    userId: PropTypes.string.isRequired,
    history: PropTypes.shape().isRequired,
    closeReelCreationModal: PropTypes.func.isRequired,
};

CreationModal.defaultProps = {
    createReelLoading: undefined,
    createReelData: undefined,
    createReelError: undefined,
};

export default CreationModal;
